import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { Size } from "@themes/Device";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import { reloadPage } from "@util/BrowserUtil";
import { buildRoute } from "@util/RouteUtil";
import { AdminStreakInput } from "./AdminStreakInput";

const Root = styled.div`
    min-height: 100dvh;
    background: ${({ theme }) => theme.backgroundPrimary};
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Container = styled.main`
    padding: 24px;
    width: clamp(0px, ${Size.laptop}, 100%);
`;

const SectionHeader = styled.h2`
    margin-top: 16px;
`;

const ManagementGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr 80px [end];
    max-width: 400px;
`;

const Row = styled.div`
    grid-column: 1 / end;
    display: grid;
    align-items: center;
    grid-template-columns: subgrid;
    gap: 8px;

    &:not(:first-child) {
        border-top: 1px solid ${({ theme }) => theme.borderPrimary};
    }
`;

const RawData = styled.div`
    font-family: monospace;
    max-width: 120ch;
    height: 300px;
    overflow: auto;
    ${ScrollbarStyles}
`;

type AdminUserControlsProps = {
    uid: string;
    userData: UserEntity;
};

export function AdminUserControls(props: AdminUserControlsProps) {
    const history = useHistory();

    const handleBack = () => {
        history.push(buildRoute(Routes.Admin));
    };

    const toggleIsProUser = () => {
        callMonolith("admin-change-pro-status", { uid: props.uid, isProUser: !props.userData.proUserInfo?.isProUser })
            .then(() => {
                reloadPage();
            })
            .catch(() => alert("Failed to toggle pro status"));
    };

    const resetFailedPayments = () => {
        callMonolith("admin-reset-failed-payment-count", { uid: props.uid })
            .then(() => {
                reloadPage();
            })
            .catch(() => alert("Failed to reset failed payments"));
    };

    return (
        <Root>
            <Container>
                <LifeAtButtonV2 onClick={handleBack}>{"<"} back</LifeAtButtonV2>
                <h1>{props.userData.displayName}</h1>
                <p>{props.userData.email}</p>
                <p>{props.uid}</p>
                <SectionHeader>Pro User Management</SectionHeader>
                <ManagementGrid>
                    <Row>
                        <span>Pro Status</span>
                        <span>{props.userData.proUserInfo?.isProUser ? "Pro" : "Free"}</span>
                        <LifeAtButtonV2 fullWidth onClick={toggleIsProUser}>
                            Toggle
                        </LifeAtButtonV2>
                    </Row>
                    <Row>
                        <span>Failed Payments #</span>
                        <span>{props.userData.proUserInfo?.numberOfFailedPayments ?? "N/A"}</span>
                        <LifeAtButtonV2 fullWidth onClick={resetFailedPayments}>
                            Reset
                        </LifeAtButtonV2>
                    </Row>
                    <Row>
                        <AdminStreakInput
                            uid={props.uid}
                            currDailyStreak={props.userData.metrics.streakMetrics.currDailyStreak}
                        />
                    </Row>
                </ManagementGrid>
                <SectionHeader>Raw Data</SectionHeader>
                <RawData>
                    <pre>{JSON.stringify(props.userData, null, 2)}</pre>
                </RawData>
            </Container>
        </Root>
    );
}
